import { useEffect, useRef, useState } from "react";

/**
 * Hook that enforces a minimum loading display duration.
 *
 * Ensures that a loading state is displayed for at least the specified duration,
 * even if the actual loading completes faster. Useful for preventing flickering
 * of loading indicators during quick operations.
 *
 * @param isLoading - The actual loading state from the operation
 * @param minimumMs - The minimum duration in milliseconds to display the loading state
 * @returns A boolean indicating whether the loading indicator should be displayed
 *
 * @example
 * ```typescript
 * const { isLoading: isLoadingFromQuery } = useQuery();
 * const isLoading = useMinimumLoadingTime(isLoadingFromQuery, 500);
 *
 * return <LoadingSpinner visible={isLoading} />;
 * ```
 */
export function useMinimumLoadingTime(isLoading: boolean, minimumMs: number) {
  const [displayLoading, setDisplayLoading] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }
      setDisplayLoading(true);
    } else if (displayLoading && startTimeRef.current) {
      // Calculate how much time has elapsed since loading started
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, minimumMs - elapsed);

      if (remaining > 0) {
        const timer = setTimeout(() => {
          setDisplayLoading(false);
          startTimeRef.current = null;
        }, remaining);
        return () => clearTimeout(timer);
      } else {
        setDisplayLoading(false);
        startTimeRef.current = null;
      }
    }
  }, [isLoading, displayLoading, minimumMs]);

  return displayLoading;
}
